import { useRef, type MouseEvent, type ReactNode } from "react";
import { cn } from "@client/lib/cn";

interface SpotlightCardProps {
  children: ReactNode;
  /** `accent` adds the design.xml gradient border and an accent top edge, for highlighted items. */
  variant?: "default" | "accent";
  className?: string;
}

/**
 * Glass card with the design.xml mouse-tracking spotlight: a 300px radial accent glow that
 * follows the cursor. Position is written to CSS variables, so no React re-render per move.
 */
export function SpotlightCard({ children, variant = "default", className }: SpotlightCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isAccent = variant === "accent";

  function handleMouseMove(event: MouseEvent<HTMLDivElement>) {
    const card = cardRef.current;
    if (!card) return;
    const bounds = card.getBoundingClientRect();
    card.style.setProperty("--spot-x", `${event.clientX - bounds.left}px`);
    card.style.setProperty("--spot-y", `${event.clientY - bounds.top}px`);
  }

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-border-default bg-linear-to-b from-surface-hover to-surface/40 shadow-card transition-all duration-300 ease-expo-out hover:-translate-y-1 hover:border-border-hover hover:shadow-card-hover",
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(300px circle at var(--spot-x, 50%) var(--spot-y, 50%), var(--color-accent-soft), transparent 70%)",
        }}
      />
      {isAccent && (
        <div aria-hidden="true" className="border-gradient-accent pointer-events-none absolute inset-0 z-10 rounded-2xl" />
      )}
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent to-transparent",
          isAccent ? "z-10 via-accent-bright" : "via-border-hover",
        )}
      />
      <div className="relative">{children}</div>
    </div>
  );
}
